import {
  copyObjectWithoutRef,
  getTemplateSource2,
} from '@common/helpers/helpers';
import { EXTERNAL_APPLICATIONS } from '@common/network/ApiPaths';
import {
  fetchTemplateCustomEmailApi,
  fetchTemplateDetailsApi,
  fetchTemplateOneSourceApi,
} from '@services/apis';
import { getByPathAndParams } from '@services/newServices';
import { create } from 'zustand';

const getNewEditorState = ({ lang, isDefault = false }) => {
  return {
    id: new Date(),
    subject: '',
    jsonData: '',
    HTMLdata: '',
    language: lang,
    isDefault,
    isValid: true,
    errorMessages: [],
  };
};

const TEMPLATE_DETAILS_INIT_STATE = Object.freeze({
  id: null,
  name: '',
  description: '',
  type: 'email', // templateType ( email , pdf_styled)
  currentLanguage: '',
  dataType: {
    id: null,
    source_id: null,
    description: '',
    external_key: '',
    name: '',
  },
  pdfAdditionalFields: { format: 'Letter', orientation: 'landscape' },
  source: {
    // depends on dataType - it is basically same thing
    structure: null,
    id: null, //  datatype_id: ''
  },
  editor: [],
  sourceTypeOptions: [],
  validation: { errors: {}, isValid: false },
});

const mapEditorState = (editorDetails) => {
  return {
    id: editorDetails?.id,
    subject: editorDetails?.subject,
    jsonData: editorDetails?.jsonData,
    HTMLdata: editorDetails?.htmlData,
    language: editorDetails?.language,
    isDefault: editorDetails?.isDefault,
    isValid: true,
    errorMessages: [],
  };
};

const mapDetailsResponseToState = (resData) => {
  const defaultLangEditor = resData?.editor?.find(({ isDefault }) => isDefault);

  return {
    id: resData?.id,
    name: resData?.name,
    description: resData?.description,
    type: resData?.type,
    dataType: resData?.dataType,
    pdfAdditionalFields: {
      format: resData?.format,
      orientation: resData?.orientation,
    },
    source: getTemplateSource2(resData?.templateSource),
    editor: resData?.editor?.map((editorState) => mapEditorState(editorState)),
    currentLanguage: defaultLangEditor?.language,
  };
};

const mapCustomEmailResponseToState = (resData) => {
  let defaultLangEditor = resData?.template?.editor?.find(
    ({ language }) =>
      language &&
      resData?.template?.preferredLanguage &&
      language?.toLowerCase() ===
        resData?.template?.preferredLanguage?.toLowerCase(),
  );

  if (!defaultLangEditor) {
    defaultLangEditor = resData?.template?.editor?.find(
      ({ isDefault }) => isDefault,
    );
  }

  return {
    id: resData?.template?.id,
    name: resData?.template?.name,
    description: '',
    type: 'email',
    dataType: resData?.dataType,
    pdfAdditionalFields: {
      format: 'A4',
      orientation: 'landscape',
    },
    source: getTemplateSource2(resData?.templateSource),
    editor: resData?.template?.editor?.map((editorState) =>
      mapEditorState(editorState),
    ),
    currentLanguage: defaultLangEditor?.language,
  };
};

export const useTemplateDetailsStore = create((set) => ({
  ...TEMPLATE_DETAILS_INIT_STATE,
  setInitState: () => {
    set({
      ...TEMPLATE_DETAILS_INIT_STATE,
    });
  },
  setFormData: ({ field, value }) => {
    set({
      [field]: value,
    });
  },
  setPdfAdditionalFields: ({ field, value }) => {
    set((state) => ({
      pdfAdditionalFields: {
        ...state.pdfAdditionalFields,
        [field]: value,
      },
    }));
  },
  setType: async (templateType) => {
    set((state) => ({
      type: templateType,
      dataType: TEMPLATE_DETAILS_INIT_STATE.dataType,
      source: TEMPLATE_DETAILS_INIT_STATE.source,
      editor: state.editor.map((editorState) => ({
        ...editorState,
        jsonData: '',
        HTMLdata: '',
      })),
    }));

    const { data } = await getByPathAndParams({
      path: EXTERNAL_APPLICATIONS.GET_SOURCES_TYPES,
      pathVariables: {
        templateType: templateType,
      },
    });

    set({ sourceTypeOptions: data });
  },
  addLanguage: (newLangValue) => {
    set((state) => {
      const isFirstAdded = !Boolean(state.editor?.length);
      if (isFirstAdded) {
        return {
          editor: [
            ...state.editor,
            {
              language: newLangValue,
              jsonData: '',
              subject: '',
              HTMLdata: '',
              isDefault: isFirstAdded,
              isValid: true,
              errorMessages: [],
            },
          ],
          currentLanguage: newLangValue,
        };
      } else {
        const defaultEditor = state?.editor?.find(({ isDefault }) => isDefault);
        return {
          editor: [
            ...state.editor,
            {
              language: newLangValue,
              jsonData: defaultEditor?.jsonData || '',
              subject: defaultEditor?.subject || '',
              HTMLdata: defaultEditor?.HTMLdata || '',
              isDefault: false,
              isValid: true,
              errorMessages: [],
            },
          ],
        };
      }
    });
  },
  deleteLanguage: (delLangValue) => {
    set((state) => {
      return {
        editor: state.editor?.filter(
          ({ language }) => language !== delLangValue,
        ),
      };
    });
  },
  setDefaultTemplateLang: (newLangValue) => {
    set((state) => {
      return {
        editor: state.editor.map((editorState) => {
          if (editorState.language === newLangValue) {
            return {
              ...editorState,
              isDefault: true,
            };
          } else {
            return {
              ...editorState,
              isDefault: false,
            };
          }
        }),
        currentLanguage: newLangValue,
      };
    });
  },
  setSourceType: async ({ dataTypeId, sourceId }) => {
    set((state) => ({
      dataType: {
        id: dataTypeId,
        source_id: sourceId,
      },
      editor: state.editor.map((editorState) => ({
        ...editorState,
        jsonData: '',
        subject: '',
        HTMLdata: '',
      })),
    }));

    const structure = await fetchTemplateOneSourceApi(sourceId);

    set({
      source: {
        id: sourceId,
        structure,
      },
    });
  },
  setEditorDataOnUnmount: ({
    jsonData,
    HTMLdata,
    language,
    isValid,
    errorMessages,
  }) => {
    set((state) => ({
      editor: state.editor.map((editorState) => {
        if (editorState.language === language) {
          return {
            ...editorState,
            jsonData,
            HTMLdata,
            language,
            isValid,
            errorMessages,
          };
        }
        return editorState;
      }),
    }));
  },
  changeTemplateLanguage: (lang) => {
    set((state) => {
      const copyOfEditorState = copyObjectWithoutRef(state.editor);
      let isLangAlreadyExist = false;
      const newEditorState = copyOfEditorState.reduce(
        (aggState, currentLangTemplate) => {
          if (currentLangTemplate?.language === lang) {
            currentLangTemplate.isDefault = true;
            isLangAlreadyExist = true;
          } else {
            currentLangTemplate.isDefault = false;
          }

          aggState.push(currentLangTemplate);
          return aggState;
        },
        [],
      );

      if (!isLangAlreadyExist) {
        newEditorState.push(getNewEditorState({ lang, isDefault: true }));
      }

      return {
        editor: newEditorState,
      };
    });
  },
  setCurrentEditorSubject: ({ currentLanguage, newSubject }) => {
    set((state) => {
      return {
        editor: state?.editor?.map((editorState) => {
          if (editorState?.language === currentLanguage) {
            editorState.subject = newSubject || null;
          }
          return editorState;
        }),
      };
    });
  },
  setTemplateDetailsInEdit: async (templateId) => {
    const newTemplateDetails = await fetchTemplateDetailsApi(templateId);

    const detailsFormatted = mapDetailsResponseToState(newTemplateDetails);

    const { data } = await getByPathAndParams({
      path: EXTERNAL_APPLICATIONS.GET_SOURCES_TYPES,
      pathVariables: {
        templateType: detailsFormatted?.type,
      },
    });

    set({
      ...detailsFormatted,
      sourceTypeOptions: data,
    });
  },
  setTemplateDetailsInCustomEmail: async () => {
    const newTemplateDetails = await fetchTemplateCustomEmailApi();

    const detailsFormatted = mapCustomEmailResponseToState(newTemplateDetails);

    const { data } = await getByPathAndParams({
      path: EXTERNAL_APPLICATIONS.GET_SOURCES_TYPES,
      pathVariables: {
        templateType: detailsFormatted?.type,
      },
    });

    set({
      ...detailsFormatted,
      sourceTypeOptions: data,
    });
  },
}));
